import { api } from './client'
import type { FileRecord } from './files-api'

// GrowerArea is a single tea-cultivation parcel (vùng trồng) belonging to a grower
// household. `id` is the stable identifier foreign references (care-log parcels)
// point to — `code` is only a human-facing label and must never be used as a key.
// New areas may omit `id` (the server mints one); existing areas MUST round-trip
// their id or the references break. `code` ("MVTNNN") is auto-generated by the
// server and never entered in the UI — new areas send it empty and the server
// mints it. paths/markers carry optional geo-coordinates preserved verbatim on
// edit to avoid data loss.
export interface GrowerArea {
    id?: string
    code?: string
    name: string
    acreage: number
    note?: string
    paths?: unknown[]
    markers?: unknown[]
}

/**
 * Ô upload nào giữ tệp này: `profile` là giấy tờ nhân thân/hồ sơ của hộ (CCCD…),
 * `land` là giấy tờ đất của MỘT vùng trồng (bìa đỏ…). Chung một bảng
 * `grower_files` nên thiếu phân loại là 2 ô hiển thị y hệt nhau.
 */
export type GrowerFileKind = 'profile' | 'land'

/**
 * File đính kèm của hộ = FileRecord + phân loại ô upload + vùng trồng giữ nó.
 *
 * `area_id` chỉ có với kind 'land'. Rỗng trên một tệp 'land' là tệp mồ côi từ
 * trước khi tách giấy tờ đất theo vùng (hộ chưa khai vùng nào lúc chuyển đổi) —
 * không màn nào hiển thị nó nữa.
 */
export type GrowerFileRecord = FileRecord & {
    kind?: GrowerFileKind
    area_id?: string | null
}

export interface Grower {
    id: string
    name: string
    /** Tên thường gọi ở địa phương ("Bà Tư") — dùng để phân biệt các hộ trùng
     *  tên khai sinh. Không bắt buộc: hộ cũ không có. */
    nickname?: string
    birthday?: string
    phone?: string
    address?: string
    national_id?: string
    national_id_issued_date?: string
    national_id_issued_place?: string
    bank_number?: string
    bank_name?: string
    bank_account?: string
    areas: GrowerArea[]
    user_id?: string
    created_at: string
    updated_at: string
    // Populated only by GET /growers/:id (list endpoint omits it).
    files?: GrowerFileRecord[]
}

export interface UpsertGrowerRequest {
    name: string
    nickname?: string | null
    birthday?: string | null
    phone?: string | null
    address?: string | null
    national_id?: string | null
    national_id_issued_date?: string | null
    national_id_issued_place?: string | null
    bank_number?: string | null
    bank_name?: string | null
    bank_account?: string | null
    areas: GrowerArea[]
    user_id?: string | null
}

export interface ListGrowersParams {
    search?: string
    page?: number
    per_page?: number
}

/** Màn chi tiết hộ tài khoản được mở từ trang QR vùng trồng: `full` = hồ sơ hộ
 *  quản trị (growers.read), `own` = "hộ của tôi" (hộ gắn tài khoản). */
export interface GrowerAreaGrowerLink {
    grower_id: string
    detail: 'full' | 'own'
}

export const growersApi = {
    list: (params?: ListGrowersParams) =>
        api.get<Grower[]>('/growers', {
            params: params as Record<string, string | number | undefined>,
        }),
    getById: (id: string) => api.get<Grower>(`/growers/${id}`),
    create: (data: UpsertGrowerRequest) => api.post<Grower>('/growers', data),
    update: (id: string, data: UpsertGrowerRequest) =>
        api.put<Grower>(`/growers/${id}`, data),
    delete: (id: string) => api.delete(`/growers/${id}`),
    /**
     * `areaId` BẮT BUỘC khi kind='land' — giấy tờ đất thuộc từng vùng trồng;
     * thiếu nó server trả 400. Phải bỏ trống khi kind='profile'.
     */
    attachFile: (
        id: string,
        fileId: string,
        kind?: GrowerFileKind,
        areaId?: string,
    ) =>
        api.post<void>(`/growers/${id}/files`, {
            file_id: fileId,
            kind,
            area_id: areaId,
        }),
    detachFile: (id: string, fileId: string) =>
        api.delete<void>(`/growers/${id}/files/${fileId}`),
    /** Hộ sở hữu vùng + màn chi tiết được mở; không được xem hộ → server 404. */
    areaGrowerLink: (areaId: string) =>
        api.get<GrowerAreaGrowerLink>(`/grower-areas/${areaId}/grower-link`),
}
